import { useEffect } from 'react'
import { useUpdateNodeInternals } from 'reactflow'
import { createNadDebug } from '@ponpon/api-client'

type MediaKind = 'image' | 'video'

interface RefreshMediaNodeInternalsOptions {
  nodeId: string
  projectId: string
  mediaKind: MediaKind
  nodeWidth: number
  aspectRatio: string | null
  hasMedia: boolean
}

interface MediaNodeGeometryTrace extends Record<string, string | number | boolean> {
  event: 'media-node-internals-refresh'
  trace_id: string
  project_id: string
  node_id: string
  media_kind: MediaKind
  node_width: number
  aspect_ratio: string
  has_media: boolean
}

const mediaNodeGeometryLog = createNadDebug('dw:upload-asset-edge:node-internals')
const MEDIA_NODE_GEOMETRY_NAMESPACE = 'dw:upload-asset-edge:node-internals'

function emitMediaNodeGeometryTrace(data: MediaNodeGeometryTrace) {
  if (!import.meta.env.VITE_NAD_DEBUG) {
    mediaNodeGeometryLog(data)
    return
  }

  const entry = {
    ...data,
    feature: 'upload-asset-edge',
    concern: 'node-internals',
  }
  fetch('/api/debug-log', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify([
      {
        ts: Date.now(),
        ns: MEDIA_NODE_GEOMETRY_NAMESPACE,
        args: [entry],
      },
    ]),
    keepalive: true,
  }).catch(() => {})
}

export function useRefreshMediaNodeInternals({
  nodeId,
  projectId,
  mediaKind,
  nodeWidth,
  aspectRatio,
  hasMedia,
}: RefreshMediaNodeInternalsOptions) {
  const updateNodeInternals = useUpdateNodeInternals()

  useEffect(() => {
    const refresh = () => {
      updateNodeInternals(nodeId)
      emitMediaNodeGeometryTrace({
        event: 'media-node-internals-refresh',
        trace_id: `upload-asset-edge:${nodeId}`,
        project_id: projectId,
        node_id: nodeId,
        media_kind: mediaKind,
        node_width: nodeWidth,
        aspect_ratio: aspectRatio ?? 'default',
        has_media: hasMedia,
      })
    }

    const frame = window.requestAnimationFrame(refresh)
    return () => window.cancelAnimationFrame(frame)
  }, [aspectRatio, hasMedia, mediaKind, nodeId, nodeWidth, projectId, updateNodeInternals])
}
